/**
 * Общие утилиты валидации для форм
 */
// Регулярные выражения для валидации
export const VALIDATION_PATTERNS = {
    email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    phone: /^[\d\s\+\-\(\)]+$/,
    // Базовая проверка URL (можно расширить)
    url: /^https?:\/\/.+/,
};
// Сообщения об ошибках валидации
export const VALIDATION_MESSAGES = {
    email: 'Некорректный формат email',
    phone: 'Телефон может содержать только цифры и символы +, -, (, ), пробел',
    website: 'Некорректный формат веб-сайта',
    required: 'Это поле обязательно для заполнения',
    maxLength: (max) => `Максимальная длина ${max} символов`,
    minLength: (min) => `Минимальная длина ${min} символов`,
};
/**
 * Создает валидатор email для Ant Design Form
 */
export const createEmailValidator = (required = false) => {
    return (_, value) => {
        // Если поле не обязательное и пустое - валидация пройдена
        if (!required && !value) {
            return Promise.resolve();
        }
        // Если поле обязательное и пустое - ошибка
        if (required && !value) {
            return Promise.reject(new Error(VALIDATION_MESSAGES.required));
        }
        // Проверяем формат email
        if (value && !VALIDATION_PATTERNS.email.test(value)) {
            return Promise.reject(new Error(VALIDATION_MESSAGES.email));
        }
        return Promise.resolve();
    };
};
/**
 * Создает валидатор телефона для Ant Design Form
 */
export const createPhoneValidator = (required = false) => {
    return (_, value) => {
        // Если поле не обязательное и пустое - валидация пройдена
        if (!required && !value) {
            return Promise.resolve();
        }
        // Если поле обязательное и пустое - ошибка
        if (required && !value) {
            return Promise.reject(new Error(VALIDATION_MESSAGES.required));
        }
        // Проверяем формат телефона
        if (value && !VALIDATION_PATTERNS.phone.test(value)) {
            return Promise.reject(new Error(VALIDATION_MESSAGES.phone));
        }
        return Promise.resolve();
    };
};
/**
 * Создает валидатор веб-сайта для Ant Design Form
 */
export const createWebsiteValidator = (required = false) => {
    return (_, value) => {
        // Если поле не обязательное и пустое - валидация пройдена
        if (!required && !value) {
            return Promise.resolve();
        }
        // Если поле обязательное и пустое - ошибка
        if (required && !value) {
            return Promise.reject(new Error(VALIDATION_MESSAGES.required));
        }
        // Проверяем формат URL
        if (value) {
            try {
                new URL(value);
                return Promise.resolve();
            }
            catch {
                return Promise.reject(new Error(VALIDATION_MESSAGES.website));
            }
        }
        return Promise.resolve();
    };
};
/**
 * Создает валидатор длины строки для Ant Design Form
 */
export const createLengthValidator = (maxLength, minLength, required = false) => {
    return (_, value) => {
        // Если поле не обязательное и пустое - валидация пройдена
        if (!required && !value) {
            return Promise.resolve();
        }
        // Если поле обязательное и пустое - ошибка
        if (required && !value) {
            return Promise.reject(new Error(VALIDATION_MESSAGES.required));
        }
        if (value) {
            // Проверяем минимальную длину
            if (minLength && value.length < minLength) {
                return Promise.reject(new Error(VALIDATION_MESSAGES.minLength(minLength)));
            }
            // Проверяем максимальную длину
            if (maxLength && value.length > maxLength) {
                return Promise.reject(new Error(VALIDATION_MESSAGES.maxLength(maxLength)));
            }
        }
        return Promise.resolve();
    };
};
/**
 * Комбинированный валидатор для email с ограничением длины
 */
export const createEmailLengthValidator = (maxLength = 100, required = false) => {
    return (_, value) => {
        // Если поле не обязательное и пустое - валидация пройдена
        if (!required && !value) {
            return Promise.resolve();
        }
        // Если поле обязательное и пустое - ошибка
        if (required && !value) {
            return Promise.reject(new Error(VALIDATION_MESSAGES.required));
        }
        if (value) {
            // Проверяем длину
            if (value.length > maxLength) {
                return Promise.reject(new Error(VALIDATION_MESSAGES.maxLength(maxLength)));
            }
            // Проверяем формат email
            if (!VALIDATION_PATTERNS.email.test(value)) {
                return Promise.reject(new Error(VALIDATION_MESSAGES.email));
            }
        }
        return Promise.resolve();
    };
};
/**
 * Комбинированный валидатор для телефона с ограничением длины
 */
export const createPhoneLengthValidator = (maxLength = 20, required = false) => {
    return (_, value) => {
        // Если поле не обязательное и пустое - валидация пройдена
        if (!required && !value) {
            return Promise.resolve();
        }
        // Если поле обязательное и пустое - ошибка
        if (required && !value) {
            return Promise.reject(new Error(VALIDATION_MESSAGES.required));
        }
        if (value) {
            // Проверяем длину
            if (value.length > maxLength) {
                return Promise.reject(new Error(VALIDATION_MESSAGES.maxLength(maxLength)));
            }
            // Проверяем формат телефона
            if (!VALIDATION_PATTERNS.phone.test(value)) {
                return Promise.reject(new Error(VALIDATION_MESSAGES.phone));
            }
        }
        return Promise.resolve();
    };
};
/**
 * Комбинированный валидатор для веб-сайта с ограничением длины
 */
export const createWebsiteLengthValidator = (maxLength = 255, required = false) => {
    return (_, value) => {
        // Если поле не обязательное и пустое - валидация пройдена
        if (!required && !value) {
            return Promise.resolve();
        }
        // Если поле обязательное и пустое - ошибка
        if (required && !value) {
            return Promise.reject(new Error(VALIDATION_MESSAGES.required));
        }
        if (value) {
            // Проверяем длину
            if (value.length > maxLength) {
                return Promise.reject(new Error(VALIDATION_MESSAGES.maxLength(maxLength)));
            }
            // Проверяем формат URL
            try {
                new URL(value);
            }
            catch {
                return Promise.reject(new Error(VALIDATION_MESSAGES.website));
            }
        }
        return Promise.resolve();
    };
};
/**
 * Утилита для очистки строковых данных формы
 * Удаляет пробелы в начале и конце, возвращает undefined для пустых строк
 */
export const sanitizeFormData = (data) => {
    const sanitized = {};
    for (const [key, value] of Object.entries(data)) {
        if (typeof value === 'string') {
            const trimmed = value.trim();
            sanitized[key] = (trimmed || undefined);
        }
        else {
            sanitized[key] = value;
        }
    }
    return sanitized;
};
